import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { usePagedList, useDebouncedValue } from "@/lib/usePagedList";
import LoadMoreButton from "@/components/LoadMoreButton";
import NewsCard from "@/components/NewsCard";
import SeoHead from "@/components/SeoHead";
import PageHero from "@/components/PageHero";
import { withDistinctImages } from "@/lib/imagePicker";
import { Search } from "lucide-react";

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="flex flex-col gap-1 text-[12px]">
    <span className="dl-overline">{label}</span>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:border-[#111827]"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </label>
);

const News = () => {
  const { t, lang } = useLang();
  const location = useLocation();
  const initialQ = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search]);
  const [filters, setFilters] = useState({ category: "all", since: "all", q: initialQ });
  const dq = useDebouncedValue(filters.q);
  const { items, loading, loadingMore, hasMore, loadMore } = usePagedList("/news", { ...filters, q: dq.trim(), language: lang });

  const allOpt = { value: "all", label: t.filters.all };
  const TOPICS_EN = ["AI in Supply Chain", "Planning", "Inventory", "S&OP / IBP", "Sourcing / Procurement", "Manufacturing", "Warehousing / Logistics", "IT & AI Governance"];
  const categoryOpts = [allOpt, ...TOPICS_EN.map((v, i) => ({ value: v, label: t.topics[i] || v }))];
  const periodOpts = [
    { value: "all", label: t.filters.periodAll },
    { value: "today", label: t.filters.periodToday },
    { value: "week", label: t.filters.periodWeek },
    { value: "month", label: t.filters.periodMonth },
    { value: "quarter", label: t.filters.periodQuarter },
    { value: "year", label: t.filters.periodYear },
  ];

  return (
    <div data-testid="news-page">
      <SeoHead
        lang={lang}
        path="/news"
        title={lang === "fr" ? "Actualités Supply Chain IA" : "Supply Chain AI news"}
        description={lang === "fr"
          ? "Suivez les actualités IA en Supply Chain, planification, S&OP, prévisions et gouvernance. Curation Dataleo pour les directions Opérations."
          : "Track AI news in Supply Chain, planning, S&OP, forecasting and governance. Curated by Dataleo for Operations leaders."}
      />
      <PageHero
        kicker={lang === "fr" ? "Flux · Actualités" : "Feed · News"}
        title={t.nav.news}
        description={t.tagline}
        accent="green"
        testId="news-hero"
      />
      <div className="dl-container py-10">

      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div className="flex flex-col gap-1 text-[12px] flex-1 min-w-[220px]">
          <span className="dl-overline">{t.filters.search}</span>
          <div className="flex items-center border border-[#E5E7EB] bg-white">
            <Search size={16} className="ml-3 text-[#4B5563]" />
            <input
              data-testid="filter-search"
              type="text"
              placeholder={t.filters.search}
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="flex-1 px-2 py-2 text-sm bg-transparent outline-none"
            />
          </div>
        </div>
        <Select testid="filter-category" label={t.filters.category} value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={categoryOpts} />
        <Select testid="filter-period" label={t.filters.period} value={filters.since} onChange={(v) => setFilters({ ...filters, since: v })} options={periodOpts} />
        <button
          data-testid="filter-reset"
          onClick={() => setFilters({ category: "all", since: "all", q: "" })}
          className="text-sm underline-offset-4 hover:underline text-[#4B5563]"
        >
          {t.filters.reset}
        </button>
      </div>

      {loading ? (
        <div className="text-[#4B5563]">…</div>
      ) : items.length === 0 ? (
        <div data-testid="news-empty" className="text-[#4B5563] py-12 text-center">No results.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6" data-testid="news-grid">
          {withDistinctImages(items, "news").map((n, i) => <NewsCard key={n.id} item={n} idx={i} />)}
        </div>
      )}
      {!loading && <LoadMoreButton hasMore={hasMore} loading={loadingMore} onClick={loadMore} lang={lang} testid="news-load-more" />}
      </div>
    </div>
  );
};

export default News;
